'use client';

import * as DS from '@aurorasolar/ds';

import { useCurrency } from '@/hooks/useCurrency';

import { SimulationResult } from '../engine';

type Props = {
  result?: SimulationResult;
};

const formatPayback = (result: SimulationResult | undefined, hasPlan: boolean): string => {
  if (!result || !hasPlan) {
    return '—';
  }
  if (result.paybackYears === null) {
    return result.annualSavingsUsd > 0 ? '25+ yrs' : 'n/a';
  }
  return `${result.paybackYears} yrs`;
};

const SummaryCard = ({ detail, label, value }: { detail?: string; label: string; value: string }) => (
  <DS.Flex bg="uiBgAlternate" borderRadius={8} flex={1} gap={1} minW={0} p={{ _: 3, md: 5 }}>
    <DS.Text color="uiHelperCopy" text="body12">
      {label}
    </DS.Text>
    <DS.Text text={{ _: 'h4', md: 'h3' }}>{value}</DS.Text>
    {detail ? (
      <DS.Text color="uiHelperCopy" text="body12">
        {detail}
      </DS.Text>
    ) : null}
  </DS.Flex>
);

export const ResultsSummary = ({ result }: Props) => {
  const { formatCurrency } = useCurrency();

  const hasPlan = result !== undefined && result.measureCosts.length > 0;
  const savings = hasPlan ? formatCurrency(Math.round(result.annualSavingsUsd)) : '—';
  const upfront = hasPlan ? formatCurrency(Math.round(result.upfrontNetUsd)) : '—';

  const backupDetail =
    result?.assumptions.backupHours && result.assumptions.backupHours > 0
      ? `~${Math.round(result.assumptions.backupHours)} hrs of backup power`
      : undefined;
  const solarDetail = result?.solarSizeKw ? `${result.solarSizeKw} kW solar system` : undefined;

  return (
    <DS.Flex gap={2} w="100%">
      <DS.Grid columnGap={3} gridTemplateColumns="repeat(3, 1fr)" w="100%">
        <SummaryCard detail={solarDetail} label="Annual savings" value={savings} />
        <SummaryCard detail="Before state or utility incentives" label="Upfront investment" value={upfront} />
        <SummaryCard detail={backupDetail} label="Payback period" value={formatPayback(result, hasPlan)} />
      </DS.Grid>
    </DS.Flex>
  );
};
